/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import { Box, Button, IconButton, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import configData from "config/config.json";
import { getPlayerCardUpdates } from "services/playerCardService";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import { opacity2hex, sortArray } from "utils/miscUtils";
import SearchInput from "components/SearchInput";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import { getTFGPlayerTableColumns } from "utils/tableHelper/playersTableHelper";
import useSearchHook from "hooks/useSearch";
import {
  ignoreAndIgnoreAll,
  markedAsRead,
  pullPlayerUpdates,
  readAll,
} from "services/playerService";
import { defaultPageNo } from "utils/constants";
import CustomButton from "components/CustomButton";
import useConsent from "hooks/useConsent";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import { deleteQueryParam, updateQueryParams } from "utils/queryParamsHelper";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "fit-content",
    flexFlow: "row wrap",
    gap: 4,
  },
  field: {
    width: 180,
  },
  buttonContainer: {
    minWidth: 90,
    maxWidth: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 24,
  },
  button: {
    height: 30,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    // width: "100% !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      maxWidth: "100% !important",
    },
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
}));

const RowActions = ({ data, actionHandlers }: any) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { onRead, onReadAndCreate, onIgnore } = actionHandlers;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {!(data?.isRemoved === "true") && (
          <MenuItem onClick={() => onReadAndCreate(data)}>
            Read & Create
          </MenuItem>
        )}
        <MenuItem onClick={() => onRead(data._id)}>Read</MenuItem>
        <MenuItem
          onClick={() =>
            onIgnore({ id: data._id, displayName: data?.displayName })
          }
        >
          Ignore
        </MenuItem>
      </Menu>
    </>
  );
};

const TFGPlayerUpdate = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [filteredList, setFilteredList] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");
  const [searchParams, setSearchParams] = useSearchParams();
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.PLAYER.CARD.UPDATE_ROLES
  );
  const [columns, setColumns] = useState([]);

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: filteredList?.length });

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const onRead = async (id) => {
    setLoading(true);
    try {
      const response = await markedAsRead(id);
      showSnackbar(response?.message);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error?.message ?? error, "error");
    }
  };

  const onReadAndCreate = (data: any) => {
    let path = `${configData.routes.playerDetails.createPlayerCard}?playerId=${data.playerId}&updateId=${data?._id}`;

    if (!data.isNewRec) {
      for (const key of Object.keys(data?.ratingsDelta)) {
        path += `&${key}=${data?.ratingsDelta[key]}`;
      }
    }

    navigate(path);
  };

  const fetchTFGPlayersUpdates = async () => {
    setLoading(true);
    try {
      let payload = {
        pageNo,
        pageSize,
      };

      const _sortByVariance = searchParams.get("sortKey");
      const _excludeNew = searchParams.get("excludeNew");
      if (_sortByVariance) payload["sortKey"] = _sortByVariance;
      if (searchValue) payload["search"] = searchValue;
      if (_excludeNew) payload["excludeNew"] = _excludeNew === "true";

      const response = await getPlayerCardUpdates(payload);
      setFilteredList(
        response?.items?.map((el) => ({
          ...el,
          thumbnail: el?.miniImageUrl,
          isNewRec: el?.isNewRec === null ? el?.isNewRec : `${el?.isNewRec}`,
          isRemoved:
            el?.isRemoved === null ? el?.isRemoved : `${el?.isRemoved}`,
        }))
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error?.message ?? error, "error");
    }
  };

  const sortByVariance = async () => {
    const sortVariance = searchParams.get("sortKey");
    if (sortVariance) deleteQueryParam("sortKey", setSearchParams);
    else updateQueryParams({ sortKey: "variance" }, setSearchParams);
  };

  const excludeNewClick = async () => {
    const excludeNewData = searchParams.get("excludeNew");
    if (excludeNewData === 'true') {
      deleteQueryParam("excludeNew", setSearchParams);
    } else updateQueryParams({ excludeNew: true }, setSearchParams);
  };

  const pullUpdates = async () => {
    setLoading(true);
    try {
      const resp = await pullPlayerUpdates();
      showSnackbar(resp?.data?.message);
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
      closeConsent();
      fetchTFGPlayersUpdates();
    }
  };

  const onSearch = (value: any) => {
    // reset filters if any
    onChangePage(defaultPageNo);
    _onSearch(value);
  };

  const handleExport = () => {
    fetch(
      `${import.meta.env.VITE_CMS_API_URL}/player/updates/export?token=${sessionHelper?.token}`,
      {
        method: "GET",
      }
    )
      .then((response) => {
        const contentDisposition = response.headers.get("Content-Disposition");
        const fileName = contentDisposition
          ?.split("filename=")[1]
          ?.replace(/["';]/g, "")
          .trim();

        return response.blob().then((blob) => ({ blob, fileName }));
      })
      .then(({ blob, fileName }) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => console.error("Download failed:", err));
  };

  const onReadAll = async () => {
    setLoading(true);
    try {
      const resp = await readAll();
      showSnackbar(resp?.data?.message);
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
      closeConsent();
      fetchTFGPlayersUpdates();
    }
  };

  const onIgnoreAndIgnoreAll = async () => {
    setLoading(true);
    try {
      let data = constentDetails.id ? [constentDetails.id] : null;

      const resp = await ignoreAndIgnoreAll(data);

      showSnackbar(resp?.data?.message);
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
      closeConsent();
      fetchTFGPlayersUpdates();
    }
  };

  const _openConsentDialog = (properties: any) => {
    console.log({ properties });
    const { type } = properties;

    let _details = null;

    if (type === "ignoreSingle" && properties?.id) {
      _details = {
        ...constentDetails,
        id: properties?.id,
        message: `Are you sure you want to Ignore the Updates for Name:${properties?.displayName} and ID: ${properties?.id}`,
        btnText: "Ignore",
        type: type,
      };
    }

    if (type === "ignoreAll") {
      _details = {
        ...constentDetails,
        message: "Are you sure you want to Ignore All the Updates?",
        btnText: "Ignore All",
        type: type,
      };
    }

    if (type === "readAll") {
      _details = {
        ...constentDetails,
        message:
          "Read all will update TFG players, excluding the players that are removed in the update.",
        btnText: "Read All",
        type: type,
      };
    }

    if (_details) {
      updateDetails(_details);
      openConsent();
    }
  };

  const getSortByVarianceText = () => {
    const _sortByVariance = searchParams.get("sortKey");
    if (_sortByVariance) return "Clear variance sort";
    return "Sort by variance";
  };

  const getExcludeNewText = () => {
    const _sortByVariance = searchParams.get("excludeNew");
    if (_sortByVariance) return "Clear exclude new filter";
    return "Exclude new players";
  };

  const onConfirm = async () => {
    const { type } = constentDetails;
    if (["ignoreAll", "ignoreSingle"].includes(type))
      await onIgnoreAndIgnoreAll();
    if (type === "readAll") await onReadAll();
  };

  useEffect(() => {
    fetchTFGPlayersUpdates();
    const cols = getTFGPlayerTableColumns({});
    setColumns(cols.filter((col) => col.id !== "actions" || hasEditPermission));
  }, [pageNo, pageSize, searchValue, searchParams.toString()]);

  return (
    <ContentShell
      title="TFG Player Updates"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a player card"
            onSearch={onSearch}
          />
          <CustomButton onClick={handleExport} startIcon>
            Export all (XLSX)
          </CustomButton>
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <Box className={styles.root}>
          <Button
            className={styles.button}
            onClick={() => _openConsentDialog({ type: "readAll" })}
          >
            Read All
          </Button>
          <Button
            className={styles.button}
            onClick={() => _openConsentDialog({ type: "ignoreAll" })}
          >
            Ignore All
          </Button>
          <Button className={styles.button} onClick={sortByVariance}>
            {getSortByVarianceText()}
          </Button>
          <Button className={styles.button} onClick={pullUpdates}>
            Pull Updates
          </Button>
          <Button className={styles.button} onClick={excludeNewClick}>
            {getExcludeNewText()}
          </Button>
        </Box>
        <Table
          columns={columns}
          data={filteredList}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={filteredList.length}
          highlightAltRow={true}
          actions={RowActions}
          onColumnClick={onColumnClick}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          actionHandlers={{
            onRead: onRead,
            onIgnore: (d) => _openConsentDialog({ type: "ignoreSingle", ...d }),
            onReadAndCreate: onReadAndCreate,
          }}
        />
        <DeleteConsentDialog
          btnText={constentDetails?.btnText}
          message={constentDetails?.message}
          itemName={constentDetails?.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onConfirm}
        />
      </Box>
    </ContentShell>
  );
};

export default TFGPlayerUpdate;
